import { ESP_ACTIVITY_TYPE_OPTIONS } from '../configs/options'
import { IEspActivity, IEspSponsorship, TEspActivityType } from '../model/types'

import { getEspSponsorKey } from './mapEspResponse'

const DAY_MS = 24 * 60 * 60 * 1000

/** A sponsored partner is flagged for attention once its last activity is older than this. */
export const ESP_STALE_ACTIVITY_DAYS = 90

export interface IEspTypeShare {
  key: TEspActivityType
  label: string
  count: number
  /** Share of all activities, 0–100. */
  percent: number
}

export interface IEspSponsorRow {
  /** See getEspSponsorKey: the email when there is one, the name otherwise. */
  sponsorKey: string
  sponsorName: string
  partnersCount: number
  /** Sponsored partners with activity / sponsored partners, 0–100; null when none. */
  coveragePercent: number | null
  /** ISO date of the latest activity over the sponsor's partners, or null when nothing was logged. */
  lastActivityDate: string | null
}

export interface IEspAttentionRow {
  partnerId: string
  partnerName: string
  sponsorName: string
  /** ISO date of the most recent activity, or null when nothing was ever logged. */
  lastActivityDate: string | null
  activitiesCount: number
}

export interface IEspDashboardStats {
  sponsoredPartnersCount: number
  partnersWithActivityCount: number
  partnersWithoutActivityCount: number
  /** partnersWithActivity / sponsoredPartners, 0–100; null when nothing is sponsored. */
  coveragePercent: number | null
  totalActivities: number
  byType: IEspTypeShare[]
  bySponsor: IEspSponsorRow[]
  needingAttention: IEspAttentionRow[]
}

const percentOf = (part: number, total: number): number | null =>
  total ? Math.round((part / total) * 100) : null

const groupActivitiesByPartner = (activities: IEspActivity[]): Map<string, IEspActivity[]> => {
  const byPartner = new Map<string, IEspActivity[]>()

  activities.forEach((activity) => {
    const partnerActivities = byPartner.get(activity.partnerId)

    if (partnerActivities) {
      partnerActivities.push(activity)
    } else {
      byPartner.set(activity.partnerId, [activity])
    }
  })

  return byPartner
}

const getLastActivityDate = (activities: IEspActivity[]): string | null =>
  activities.reduce<string | null>(
    (latest, activity) => (!latest || activity.date > latest ? activity.date : latest),
    null
  )

/**
 * Everything the executive dashboard shows, computed in one pass over the service data:
 * headline coverage numbers, the activity-type breakdown, the per-sponsor roll-up and
 * the partners that need attention (nothing logged, or nothing logged recently).
 *
 * Pass already-filtered data to scope the whole dashboard to a single sponsor.
 */
export const getEspDashboardStats = (
  activities: IEspActivity[],
  sponsorships: IEspSponsorship[],
  partnerNames: Record<string, string>,
  now: number
): IEspDashboardStats => {
  const sponsored = sponsorships.filter(
    (
      sponsorship
    ): sponsorship is IEspSponsorship & {
      currentSponsor: NonNullable<IEspSponsorship['currentSponsor']>
    } => Boolean(sponsorship.currentSponsor)
  )
  const activitiesByPartner = groupActivitiesByPartner(activities)

  const hasActivity = (partnerId: string) => Boolean(activitiesByPartner.get(partnerId)?.length)

  const partnersWithActivityCount = sponsored.filter((sponsorship) =>
    hasActivity(sponsorship.partnerId)
  ).length

  const byType: IEspTypeShare[] = ESP_ACTIVITY_TYPE_OPTIONS.map((option) => {
    const count = activities.filter((activity) => activity.activityType === option.key).length

    return {
      key: option.key,
      label: option.value,
      count,
      percent: percentOf(count, activities.length) ?? 0,
    }
  })

  const sponsorKeys = [
    ...new Set(sponsored.map((sponsorship) => getEspSponsorKey(sponsorship.currentSponsor))),
  ]

  const bySponsor: IEspSponsorRow[] = sponsorKeys
    .map((sponsorKey) => {
      const partners = sponsored.filter(
        (sponsorship) => getEspSponsorKey(sponsorship.currentSponsor) === sponsorKey
      )
      const sponsorActivities = partners.flatMap(
        (sponsorship) => activitiesByPartner.get(sponsorship.partnerId) ?? []
      )

      return {
        sponsorKey,
        sponsorName: partners[0].currentSponsor.name,
        partnersCount: partners.length,
        coveragePercent: percentOf(
          partners.filter((sponsorship) => hasActivity(sponsorship.partnerId)).length,
          partners.length
        ),
        lastActivityDate: getLastActivityDate(sponsorActivities),
      }
    })
    .sort((a, b) => b.partnersCount - a.partnersCount || a.sponsorName.localeCompare(b.sponsorName))

  const staleBefore = now - ESP_STALE_ACTIVITY_DAYS * DAY_MS

  const needingAttention: IEspAttentionRow[] = sponsored
    .map((sponsorship) => {
      const partnerActivities = activitiesByPartner.get(sponsorship.partnerId) ?? []

      return {
        partnerId: sponsorship.partnerId,
        partnerName: partnerNames[sponsorship.partnerId] ?? sponsorship.partnerId,
        sponsorName: sponsorship.currentSponsor.name,
        lastActivityDate: getLastActivityDate(partnerActivities),
        activitiesCount: partnerActivities.length,
      }
    })
    .filter((row) => !row.lastActivityDate || Date.parse(row.lastActivityDate) < staleBefore)
    .sort((a, b) => {
      // Never-logged partners first, then the stalest ones.
      if (!a.lastActivityDate || !b.lastActivityDate) {
        return Number(Boolean(a.lastActivityDate)) - Number(Boolean(b.lastActivityDate))
      }

      return a.lastActivityDate.localeCompare(b.lastActivityDate)
    })

  return {
    sponsoredPartnersCount: sponsored.length,
    partnersWithActivityCount,
    partnersWithoutActivityCount: sponsored.length - partnersWithActivityCount,
    coveragePercent: percentOf(partnersWithActivityCount, sponsored.length),
    totalActivities: activities.length,
    byType,
    bySponsor,
    needingAttention,
  }
}
